import { createSelector } from '@reduxjs/toolkit';
import { DataStatus, isError, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as RolesAPI from '../api/rolesAPI';
import type { CreateRoleParams, Role } from './types';

interface State {
  roles:
    | {
        data: Role[];
        status: DataStatus.finished;
      }
    | {
        data: null;
        status: Exclude<DataStatus, DataStatus.finished>;
      };
}

const initialState: State = {
  roles: {
    data: null,
    status: DataStatus.idle,
  },
};

export const rolesSlice = createAppSlice({
  name: 'roles',
  initialState,
  reducers: (create) => ({
    setupRoles: create.asyncThunk(
      (_: void) => {
        return RolesAPI.getRoles();
      },
      {
        pending: (state) => {
          state.roles.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.roles.data = action.payload;
          state.roles.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.roles.status = DataStatus.error;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (isLoading(state.roles.roles.status)) {
              return false;
            }
          },
        },
      },
    ),
    createRole: create.asyncThunk<CreateRoleParams, Role>(
      (params) => {
        return RolesAPI.createRole(params);
      },
      {
        pending: (state) => {
          state.roles.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          if (state.roles.data) {
            state.roles.data.push(action.payload);
            state.roles.status = DataStatus.finished;
          } else {
            state.roles = { data: [action.payload], status: DataStatus.finished };
          }
        },
        rejected: (state) => {
          state.roles.status = DataStatus.finished;
        },
      },
    ),
    updateRole: create.asyncThunk<{ id: string; params: CreateRoleParams }, Role>(
      ({ id, params }) => {
        return RolesAPI.updateRole(id, params);
      },
      {
        pending: (state) => {
          state.roles.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          if (state.roles.data) {
            state.roles.data = state.roles.data.map((role) =>
              role.id === action.payload.id ? action.payload : role,
            );
          }
          state.roles.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.roles.status = DataStatus.finished;
        },
      },
    ),
    deleteRole: create.asyncThunk<string, void>(
      (id) => {
        return RolesAPI.deleteRole(id);
      },
      {
        pending: (state) => {
          state.roles.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          if (!state.roles.data) {
            return;
          }

          state.roles.data = state.roles.data.filter(
            (role) => role.id !== action.meta.arg,
          );
          state.roles.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.roles.status = DataStatus.finished;
        },
      },
    ),
  }),
  selectors: {
    selectRoles: (state) => {
      return state.roles.data;
    },
    selectIsRolesLoading: (state) => {
      return isLoading(state.roles.status);
    },
    selectIsRolesFailed: (state) => {
      return isError(state.roles.status);
    },
    selectRoleById: createSelector(
      (state) => state.roles.data,
      (_, roleId: string) => roleId,
      (roles: Role[] | null, roleId: string) => {
        return roles?.find((role) => role.id === roleId);
      },
    ),
  },
});

export const { setupRoles, createRole, updateRole, deleteRole } = rolesSlice.actions;
export const {
  selectRoles,
  selectIsRolesLoading,
  selectIsRolesFailed,
  selectRoleById,
} = rolesSlice.selectors;
